import axios, { AxiosResponse } from 'axios';
import pathfinder from '@fiverr-private/pathfinder';
import { Gql } from '@fiverr-private/dynamic_matching';
import { z } from '@fiverr-private/forms';
import type { InvalidIntent } from '../i18n';
import { handlePollingError } from '../hooks/classification/utils';
import { logger } from '../utils/logger';
import { BriefNeedType } from '../components/Sections/NeedType/types';
import type { DynamicMatchingCreationResponse, Extractor, PollingResponse, ExperimentAllocation } from '.';

export interface LeafClassification {
    category_id: number;
    sub_category_id: number;
    nested_sub_category_id: number;
    display_name: string;
    explainer: string;
}

export interface MetadataClassification {
    filters: Record<string, string[]>;
}

export type IntentClassification = InvalidIntent | 'valid_intent';

export interface IntentClassificationResponse {
    classification: IntentClassification;
}

export interface MetadataAnswerType {
    slug: string;
}

export interface EnrichedMetadataAnswerType extends MetadataAnswerType {
    display_name: string;
}

export interface MetadataQuestionType {
    metadata_type: string;
    selection_type: 'multi_select' | 'single_select';
    answers: EnrichedMetadataAnswerType[];
    answers_limit: number;
    display_priority: number;
}

export interface EnrichedMetadataQuestionType extends MetadataQuestionType {
    display_name: string;
    question_title: string;
}

export const dollarsToCents = (dollars: number) => dollars * 100;

export const centsSchema = z.number().transform(dollarsToCents);

export const BudgetSchema = z.object({
    validMin: centsSchema.default(0),
    validMax: centsSchema.default(Infinity),
    min: centsSchema.nullable().transform((value) => value || null),
});

export type Budget = z.infer<typeof BudgetSchema>;

export interface StaticData {
    is_hourly_eligible?: boolean;
    metadata_questions: EnrichedMetadataQuestionType[];
    hourly_budget?: Budget | null;
    budget?: Budget | null;
    fixed_ongoing_budget?: Budget | null;
}

export type BudgetConstants = Pick<StaticData, 'budget' | 'hourly_budget' | 'fixed_ongoing_budget'>;
export type BudgetType = keyof BudgetConstants;

export interface TimelineDetails {
    generalTimeline?: {
        unit?: string;
        value?: number;
    } | null;
    deliveriesFrequency?: {
        unit?: string;
        value?: number;
    } | null;
    hiringDuration?: string | null;
    timeCommitment?: string | null;
}

export interface ClassificationResolvedData {
    metadata?: MetadataClassification;
    budget: {
        min_price_in_cents: number;
        max_price_in_cents: number;
        pricing_type: Gql.BriefPricingType;
    } | null;
    query_string: string;
    delivery_time: {
        timeline: Gql.DmBriefTimeline;
        delivery_date: string;
        timeline_details?: TimelineDetails | null;
    };
    summary?: string;
    ideal_candidate?: string;
    need_type?: Partial<BriefNeedType>;
    freelancer_preferences?: {
        languages?: {
            language_code: Gql.LanguageCode;
            min_proficiency: Gql.LanguageProficiencyLevel;
        }[];
        freelancer_type?: {
            agency_preference?: Gql.DmBriefAgencyPreference;
            is_vetted_pro?: boolean;
            min_level?: Gql.DmBriefSellerLevel;
        };
        countries?: Gql.DmBriefCountryCode[];
        regions?: string[];
    };
}

export interface BriefClassificationResponse extends DynamicMatchingCreationResponse {
    leaf_classification: LeafClassification[];
    static_data?: StaticData;
    resolved_data?: ClassificationResolvedData;
    experiment_allocation?: ExperimentAllocation;
}

export interface BriefClassificationRequest extends DynamicMatchingCreationResponse {
    user_input: string;
    extractors: Extractor[];
}

export const classifyUrl = pathfinder('classify_dm_category');
export const getClassificationPollingUrl = (pollingId: string) =>
    pathfinder('classify_dm_poll', { classification_id: pollingId });

export interface BriefTierDataParams {
    budget?: number;
    pricing_type?: Gql.BriefPricingType | null;
    preferred_flow?: Gql.DmBriefTier;
}

interface ClassifyBriefParams {
    userInput: string;
    contextId: string;
    extractors: Extractor[];
    abortController: AbortController;
}

export const classifyBrief = async ({ userInput, contextId, extractors, abortController }: ClassifyBriefParams) =>
    axios.post<PollingResponse, AxiosResponse<PollingResponse>, BriefClassificationRequest>(
        classifyUrl,
        {
            user_input: userInput,
            context_id: contextId,
            extractors,
        },
        {
            signal: abortController.signal,
        }
    );

export const getClassification = async ({
    pollingId,
    inspectedField,
    abortController,
    skipDataValidation = false,
}: {
    pollingId: string;
    inspectedField: keyof BriefClassificationResponse;
    abortController?: AbortController;
    skipDataValidation?: boolean;
}) => {
    let data: any = {};

    try {
        data = await axios.get<BriefClassificationResponse>(getClassificationPollingUrl(pollingId), {
            signal: abortController?.signal,
        });
    } catch (error) {
        if (handlePollingError({ error, pollingId, inspectedField })) {
            return;
        }
    }

    if (!skipDataValidation && (!data.data || !data.data[inspectedField])) {
        throw new Error('No data');
    }

    if (data.error) {
        logger.error('Classification polling error', { error: data.error, inspectedField, pollingId });
        throw new Error(data.error);
    }

    return data;
};
